import { useState } from "react";
import { User, Tag } from "lucide-react";
import { ContentRatingToggle } from "@/components/ContentRatingToggle";
import { useUIStore } from "@/stores/uiStore";
import { cn, toTitleCase } from "@/lib/utils";
import type { FilterBarProps } from "./types";

type FilterMode = "artist" | "specialization" | null;

export function FilterBar({
  artists,
  allTags,
  selectedArtistIds,
  selectedTags,
  onArtistsChange,
  onTagsChange,
}: FilterBarProps) {
  const { selectedContentRatings, toggleContentRating } = useUIStore();
  const [filterMode, setFilterMode] = useState<FilterMode>(null);

  const handleModeClick = (mode: "artist" | "specialization") => {
    // Toggle mode - clicking same mode again hides it
    setFilterMode(filterMode === mode ? null : mode);
  };

  const handleArtistClick = (artistId: string) => {
    if (artistId === "all") {
      onArtistsChange([]);
    } else {
      if (selectedArtistIds.includes(artistId)) {
        onArtistsChange(selectedArtistIds.filter((id) => id !== artistId));
      } else {
        onArtistsChange([...selectedArtistIds, artistId]);
      }
    }
  };

  const handleTagClick = (tag: string) => {
    if (tag === "all") {
      onTagsChange([]);
    } else {
      if (selectedTags.includes(tag)) {
        onTagsChange(selectedTags.filter((t) => t !== tag));
      } else {
        onTagsChange([...selectedTags, tag]);
      }
    }
  };

  const isAllArtistsSelected = selectedArtistIds.length === 0;
  const isAllTagsSelected = selectedTags.length === 0;

  return (
    <div className="flex flex-col items-center gap-4 mb-8">
      {/* Content Rating + Filter Mode Toggle - horizontal layout */}
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <ContentRatingToggle
          selected={selectedContentRatings}
          onChange={toggleContentRating}
        />

        <div className="h-6 w-px bg-[var(--color-border)] hidden sm:block" />

        {/* Filter Mode Toggle */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-[var(--color-bg-card)] border border-[var(--color-border)]">
          <button
            onClick={() => handleModeClick("artist")}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filterMode === "artist"
                ? "bg-[var(--color-primary)] text-[var(--color-bg)]"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            )}
          >
            <User className="h-4 w-4" />
            By Artist
          </button>
          <button
            onClick={() => handleModeClick("specialization")}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filterMode === "specialization"
                ? "bg-[var(--color-primary)] text-[var(--color-bg)]"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            )}
          >
            <Tag className="h-4 w-4" />
            By Specialization
          </button>
        </div>
      </div>

      {/* Filter Buttons - only shown when a mode is selected */}
      {filterMode && (
        <div className="flex flex-wrap justify-center gap-2 max-w-4xl">
          {filterMode === "artist" ? (
            <>
              <button
                onClick={() => handleArtistClick("all")}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                  isAllArtistsSelected
                    ? "bg-[var(--color-primary)] text-[var(--color-bg)] border-[var(--color-primary)]"
                    : "bg-[var(--color-bg-card)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-primary)]"
                )}
              >
                All
              </button>
              {artists.map((artist) => (
                <button
                  key={artist.id}
                  onClick={() => handleArtistClick(artist.id)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                    selectedArtistIds.includes(artist.id)
                      ? "bg-[var(--color-primary)] text-[var(--color-bg)] border-[var(--color-primary)]"
                      : "bg-[var(--color-bg-card)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-primary)]"
                  )}
                >
                  {artist.name}
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                onClick={() => handleTagClick("all")}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                  isAllTagsSelected
                    ? "bg-[var(--color-primary)] text-[var(--color-bg)] border-[var(--color-primary)]"
                    : "bg-[var(--color-bg-card)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-primary)]"
                )}
              >
                All
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                    selectedTags.includes(tag)
                      ? "bg-[var(--color-primary)] text-[var(--color-bg)] border-[var(--color-primary)]"
                      : "bg-[var(--color-bg-card)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-primary)]"
                  )}
                >
                  {toTitleCase(tag)}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
